import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Heart, MessageCircle, Send, Trash2, Loader2 } from "lucide-react";
import { API, api, errMsg, useApi, ago } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const src = (id) => `${API}/circle/media/${id}?auth=${localStorage.getItem("dgc_token")}`;

const Composer = ({ onDone }) => {
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState(null);
  const ref = useRef(null);
  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("caption", caption);
    if (file) fd.append("file", file);
    setPct(0);
    try {
      await api.post("/circle/feed", fd, { onUploadProgress: (p) => setPct(Math.round((p.loaded / (p.total || 1)) * 100)) });
      setCaption(""); setFile(null); onDone();
      toast.success("Shared with the circle");
    } catch (err) { toast.error(errMsg(err)); }
    setPct(null);
  };
  return (
    <form onSubmit={submit} className="panel p-4" data-testid="circle-composer">
      <textarea className="field" rows={2} placeholder="Share a moment with the family…" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="circle-caption" />
      <div className="flex items-center gap-3 mt-3">
        <button type="button" onClick={() => ref.current.click()} className="btn-ghost" data-testid="circle-pick-media"><ImagePlus size={15} /> {file ? file.name.slice(0, 22) : "Photo or video"}</button>
        <input ref={ref} type="file" accept="image/*,video/*" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="circle-file-input" />
        <button disabled={pct !== null} className="btn-gold ml-auto" data-testid="circle-post-btn">{pct !== null ? <><Loader2 size={14} className="animate-spin" /> {pct}%</> : "Post"}</button>
      </div>
    </form>
  );
};

const Post = ({ p, me, reload }) => {
  const [text, setText] = useState("");
  const liked = p.likes.includes(me.id);
  const act = async (fn) => { try { await fn(); reload(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <article className="panel overflow-hidden" data-testid={`circle-post-${p.id}`}>
      <div className="p-4 flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-brand-green flex items-center justify-center font-display font-bold text-white">{p.author.replace("Dr. ", "")[0]}</span>
        <div className="flex-1"><div className="text-sm font-semibold text-ink">{p.author}</div><div className="font-data text-[10px] text-ink-3">{ago(p.created_at)}</div></div>
        {(p.author_id === me.id || ["partner", "admin"].includes(me.role)) && <button onClick={() => act(() => api.delete(`/circle/feed/${p.id}`))} className="text-ink-3 hover:text-red-500" aria-label="Delete post" data-testid={`circle-del-${p.id}`}><Trash2 size={15} /></button>}
      </div>
      {p.caption && <p className="px-4 pb-3 text-sm text-ink whitespace-pre-line">{p.caption}</p>}
      {p.media?.type === "image" && <img src={src(p.media.id)} alt="" className="w-full max-h-[560px] object-cover bg-black" loading="lazy" />}
      {p.media?.type === "video" && <video src={src(p.media.id)} controls playsInline className="w-full max-h-[560px] bg-black" />}
      <div className="px-4 py-3 flex items-center gap-4 border-t border-[color:var(--line)]">
        <button onClick={() => act(() => api.post(`/circle/feed/${p.id}/like`))} className={`flex items-center gap-1 text-sm ${liked ? "text-red-500" : "text-ink-3"}`} data-testid={`circle-like-${p.id}`}><Heart size={16} className={liked ? "fill-red-500" : ""} /> {p.likes.length}</button>
        <span className="flex items-center gap-1 text-sm text-ink-3"><MessageCircle size={16} /> {p.comments.length}</span>
      </div>
      <div className="px-4 pb-4 space-y-2">
        {p.comments.map((c) => <div key={c.id} className="text-sm"><span className="font-semibold text-ink">{c.author}</span> <span className="text-ink-3">{c.text}</span></div>)}
        <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) act(() => api.post(`/circle/feed/${p.id}/comments`, { text })).then(() => setText("")); }} className="flex gap-2">
          <input className="field !py-2" placeholder="Write a comment…" value={text} onChange={(e) => setText(e.target.value)} data-testid={`circle-comment-input-${p.id}`} />
          <button className="btn-ghost !px-3" aria-label="Send comment" data-testid={`circle-comment-send-${p.id}`}><Send size={14} /></button>
        </form>
      </div>
    </article>
  );
};

export default function Circle() {
  const { user } = useAuth();
  const { data, reload } = useApi("/circle/feed");
  const { data: people } = useApi("/circle/members");
  return (
    <div className="p-5 md:p-8 max-w-6xl grid lg:grid-cols-3 gap-6" data-testid="private-circle">
      <div className="lg:col-span-2 space-y-4">
        <div><div className="eyebrow mb-2">Private · invitation only</div><h1 className="font-display text-3xl md:text-4xl font-bold text-ink">The Circle</h1></div>
        <Composer onDone={reload} />
        {(data || []).map((p) => <Post key={p.id} p={p} me={user} reload={reload} />)}
        {data && data.length === 0 && <p className="text-sm text-ink-3">No posts yet — share the first photo or video.</p>}
      </div>
      <aside className="panel p-5 h-fit" data-testid="circle-members">
        <div className="eyebrow mb-3">Members · {(people || []).length}</div>
        {(people || []).map((m) => <div key={m.id} className="py-2 border-b border-[color:var(--line)]/60 last:border-0"><div className="text-sm text-ink font-semibold">{m.name}</div><div className="text-[11px] text-ink-3">{m.relation || m.role}</div></div>)}
      </aside>
    </div>
  );
}
